/**
 * KAIRA Panel - Conversational Mental Wellness Companion for LOGOS
 * LOGOS Companion - AI-powered conversational interface
 * Adapted from Kaira standalone app to LOGOS architecture
 */

import { useState, useEffect, useRef } from 'react';
import { useKairaLogos } from '../../hooks/kaira/useKairaLogos';

export default function KairaPanel({
  logosContext = null,
  onDimensionsExtracted = null,
  initialMode = 'checkin',
  language = 'es',
  sessionId = null,
  userId = null,
}) {
  const {
    messages,
    isLoading,
    error,
    mode,
    extractionReady,
    isExtracting,
    sendMessage,
    extractDimensions,
    switchToTherapy,
    setLogosContext,
    clearMessages,
  } = useKairaLogos(sessionId, language, userId);

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  // Set LOGOS context when provided
  useEffect(() => {
    if (logosContext) {
      setLogosContext(logosContext);
    }
  }, [logosContext, setLogosContext]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-extract dimensions when ready
  useEffect(() => {
    if (extractionReady && onDimensionsExtracted && !isExtracting) {
      const currentState = logosContext?.stateSnapshot || {};
      extractDimensions(currentState).then(data => {
        if (data?.mergedState) {
          onDimensionsExtracted(data.mergedState, data.dimensionSources);
          // Switch to therapy mode
          if (logosContext) {
            switchToTherapy(logosContext);
          }
        }
      });
    }
  }, [extractionReady, onDimensionsExtracted, isExtracting, logosContext, extractDimensions, switchToTherapy]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue);
      setInputValue('');
    }
  };

  const getModeLabel = () => {
    if (mode === 'checkin') {
      return language === 'es' ? '📊 Check-in conversacional' : '📊 Conversational Check-in';
    }
    return language === 'es' ? '🧠 Sesión de acompañamiento' : '🧠 Companion Session';
  };

  const getPlaceholder = () => {
    if (mode === 'checkin') {
      return language === 'es' 
        ? 'Cuéntame cómo has estado...'
        : 'Tell me how you\'ve been...';
    }
    return language === 'es'
      ? 'Escribe tu mensaje...'
      : 'Type your message...';
  };

  return (
    <div className="kaira-panel">
      {/* Header */}
      <div className="kaira-header">
        <div className="kaira-title">
          <span className="kaira-icon">💬</span>
          <h2>KAIRA</h2>
        </div>
        <div className="kaira-mode-badge">{getModeLabel()}</div>
      </div>

      {/* Messages */}
      <div className="kaira-messages">
        {messages.length === 0 && (
          <div className="kaira-welcome">
            <p>
              {language === 'es'
                ? 'Hola. Soy KAIRA, tu LOGOS Companion. Cuéntame cómo has estado.'
                : 'Hello. I\'m KAIRA, your LOGOS Companion. Tell me how you\'ve been.'}
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={`kaira-message kaira-message-${msg.role}`}>
            <div className="kaira-message-content">
              {msg.content}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="kaira-message kaira-message-assistant">
            <div className="kaira-message-content kaira-typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {extractionReady && !isExtracting && (
          <div className="kaira-extraction-signal">
            {language === 'es'
              ? '✨ Check-in completo. Calculando tus métricas LOGOS...'
              : '✨ Check-in complete. Calculating your LOGOS metrics...'}
          </div>
        )}

        {error && (
          <div className="kaira-error">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form className="kaira-input-form" onSubmit={handleSubmit}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={getPlaceholder()}
          disabled={isLoading || isExtracting}
          className="kaira-input"
        />
        <button
          type="submit"
          disabled={!inputValue.trim() || isLoading || isExtracting}
          className="kaira-send-button"
        >
          {isLoading ? '...' : '→'}
        </button>
      </form>
    </div>
  );
}
